import { createFileRoute } from "@tanstack/react-router"
import { useRef, useState } from "react"
import { LoadingOverlay, Group } from "@mantine/core"
import {
  DepartmentOrWorkAreaId,
  isDepartmentId,
  isWorkAreaId,
  parseDepartmentId,
  parseWorkAreaId,
} from "../../../../-components/tree/department-or-workarea-id"
import { DepartmentDutyRosterSpreadsheet } from "../../-components/DutyScheduling/duty-roster-spreadsheet"
import { SuspenseWithErrorBoundary } from "../../../../-components/suspense"
import { DatesRangeValue } from "@mantine/dates"
import { useValidatedState } from "@mantine/hooks"
import { useEffect } from "react"
import { Page } from "../../../../-components/Page"
import { DepartmentAndWorkareaTreeSelect } from "../-components/DepartmentAndWorkareaTreeSelect"
import { DatePeriodSelect } from "../orchestra/-components/period-select"
import { keepPreviousData } from "@tanstack/react-query"
import { useGridStoreInitializer } from "../../-components/DutyScheduling/state"
import { DutySchedulingContainer } from "../-components/-DutySchedulingContainer"
import { useDepartmentDutyRosterGet, useGetFormulaResults } from "@/orval/gen"
import { collectContractIds } from "../-components/collect-contracts"
import { dateToDateOnly } from "../../../../utils/date-to-date-only"
import { DateOnly } from "../../../../@client/System"

export const Route = createFileRoute("/app/duties/absences/")({
  component: RouteComponent,
})

const initialRange: [DateOnly | null, DateOnly | null] = [null, null]

function RouteComponent() {
  const [{ value: range, lastValidValue: lastValidRange }, setRange] =
    useValidatedState(initialRange, (v) => Boolean(v[0] && v[1]))

  const [selectedWorkareasOrDepartments, setSelectedWorkareasOrDepartments] =
    useState<DepartmentOrWorkAreaId[]>([])

  const departmentIds = selectedWorkareasOrDepartments
    .filter(isDepartmentId)
    .map(parseDepartmentId)
  const workareaIds = selectedWorkareasOrDepartments
    .filter(isWorkAreaId)
    .map(parseWorkAreaId)

  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (ref.current) {
      if (range[0] === null && range[1] === null) {
        const cellWidth = (1210 - 128) / 48
        const cells = Math.max(7, (ref.current.offsetWidth - 130) / cellWidth)
        const start = new Date()
        const end = new Date()
        end.setDate(new Date().getDate() + cells)
        setRange([dateToDateOnly(start), dateToDateOnly(end)])
      }
    }
  }, [])
  return (
    <Page ref={ref}>
      <Group>
        <DepartmentAndWorkareaTreeSelect
          focus={"Absences"}
          onlyDispo={false}
          mode="multiple"
          onSelectionChanged={(v) => setSelectedWorkareasOrDepartments(v)}
        />
        <DatePeriodSelect value={range} onChange={(v) => setRange(v)} />
      </Group>
      <DutySchedulingContainer>
        {lastValidRange && lastValidRange[0] && lastValidRange[1] ? (
          <InnerComponent
            from={lastValidRange[0]}
            until={lastValidRange[1]}
            departmentIds={
              departmentIds?.length === 0 ? undefined : departmentIds
            }
            workareaIds={workareaIds?.length === 0 ? undefined : workareaIds}
          />
        ) : null}
      </DutySchedulingContainer>
    </Page>
  )
}

function InnerComponent({
  from,
  until,
  departmentIds,
  workareaIds,
}: {
  from: string
  until: string
  departmentIds: number[] | undefined
  workareaIds: number[] | undefined
}) {
  const { data, refetch, isFetching, dataUpdatedAt } =
    useDepartmentDutyRosterGet(
      {
        from,
        until,
        departmentIds,
        workareaIds,
        focus: "Absences",
      },
      { query: { placeholderData: keepPreviousData } }
    )
  const departmentId = departmentIds?.[0] || undefined
  const contractIds = collectContractIds(data?.rowHeaders || [])

  const { data: formulaResults } = useGetFormulaResults(
    {
      from,
      until,
      departmentId,
      contractIds: contractIds,
      formulaIds: [],
    },
    {
      query: {
        placeholderData: keepPreviousData,
        enabled: Boolean(departmentId || workareaIds),
      },
    }
  )

  const { storeInitialized, updateFormulaResults } = useGridStoreInitializer({
    data: data,
    compact: true,
    updatedAt: dataUpdatedAt,
  })
  const formulaResultValues = formulaResults?.values

  useEffect(() => {
    if (formulaResultValues) {
      updateFormulaResults(formulaResultValues)
    }
  }, [formulaResultValues])

  return (
    <div style={{ position: "relative", minHeight: 200, minWidth: 200 }}>
      {data && storeInitialized ? (
        <SuspenseWithErrorBoundary>
          <DepartmentDutyRosterSpreadsheet
            departmentId={departmentId}
            focus="absence-planning"
            onRefetch={() => refetch()}
            data={data}
          />
        </SuspenseWithErrorBoundary>
      ) : null}

      <LoadingOverlay
        visible={isFetching}
        zIndex={15}
        overlayProps={{ radius: "sm", blur: 0 }}
      />
    </div>
  )
}
